import { Link } from '@tanstack/react-router';
import {
  ACTIVITY_SUBJECTS,
  ACTIVITY_SUBJECT_LABELS,
  REMINDER_DUE_STATES,
  REMINDER_DUE_STATE_LABELS,
  enumOptions,
  type Activity,
  type ReminderDueState,
} from '@aimmo/contracts';
import { Badge, defineResource, type ListState, type ListResult } from '@aimmo/ui';
import { Check } from 'lucide-react';
import { api, assertOk } from '../lib/api.js';
import { completeActivity } from '../lib/activities.js';
import { resolveUser, searchUsers } from './mandate-lookups.js';
import { formatDateTime } from '../lib/format.js';
import { endOfDay, startOfDay } from '../lib/dates.js';
import { subjectLink } from '../lib/subjects.js';

const DUE_TONES: Record<ReminderDueState, 'danger' | 'warning' | 'neutral'> = {
  overdue: 'danger',
  due_soon: 'warning',
  scheduled: 'neutral',
};

function asRange(value: unknown): { from?: string; to?: string } {
  if (typeof value !== 'object' || value === null) return {};
  const range: { from?: unknown; to?: unknown } = value;
  return {
    ...(typeof range.from === 'string' ? { from: range.from } : {}),
    ...(typeof range.to === 'string' ? { to: range.to } : {}),
  };
}

/**
 * The reminders worklist, as a kit list (U5).
 *
 * It has its own client rather than the CRUD adapter because two of its query
 * parameters are not filters the user sets: the type is always `reminder`, and
 * the date range filter is one control that the API reads as two bounds.
 */
async function listReminders(
  state: ListState,
  signal?: AbortSignal,
): Promise<ListResult<Activity>> {
  const filters = state.filters;
  const range = asRange(filters['scheduled']);
  const completed = filters['completed'];

  const body = assertOk(
    await api.activities.list({
      query: {
        page: state.page,
        pageSize: state.pageSize,
        order: state.order,
        sort: state.sort ?? 'scheduledAt',
        type: 'reminder',
        // Open by default: a worklist that opens full of dismissed work is not
        // a worklist. The filter can still ask for the closed ones.
        completed: typeof completed === 'boolean' ? completed : false,
        ...(state.q ? { q: state.q } : {}),
        ...(typeof filters['dueState'] === 'string'
          ? { dueState: filters['dueState'] as ReminderDueState }
          : {}),
        ...(typeof filters['subjectType'] === 'string'
          ? { subjectType: filters['subjectType'] as Activity['subjectType'] }
          : {}),
        ...(typeof filters['userId'] === 'string' ? { userId: filters['userId'] } : {}),
        ...(range.from ? { scheduledFrom: startOfDay(range.from) } : {}),
        ...(range.to ? { scheduledTo: endOfDay(range.to) } : {}),
      },
      fetchOptions: { signal },
    }),
  ).body;

  return body;
}

export const remindersResource = defineResource<Activity>({
  key: 'reminders',
  label: { one: 'Promemoria', many: 'Promemoria', gender: 'm' },
  getRowId: (row) => row.id,
  client: { list: listReminders },
  search: { placeholder: 'Testo del promemoria…' },
  defaultState: { sort: 'scheduledAt', order: 'asc', pageSize: 25 },
  columns: [
    {
      id: 'dueState',
      header: 'Stato',
      width: '9rem',
      cell: ({ row }) =>
        row.dueState ? (
          <Badge tone={DUE_TONES[row.dueState]}>{REMINDER_DUE_STATE_LABELS[row.dueState]}</Badge>
        ) : (
          <Badge tone="neutral">Chiuso</Badge>
        ),
    },
    {
      id: 'scheduledAt',
      header: 'Scadenza',
      sortKey: 'scheduledAt',
      width: '12rem',
      cell: ({ row }) => (row.scheduledAt ? formatDateTime(row.scheduledAt) : '—'),
    },
    {
      id: 'subjectLabel',
      header: 'Soggetto',
      cell: ({ row }) => (
        <Link {...subjectLink(row.subjectType, row.subjectId)} className="text-ink hover:underline">
          {row.subjectLabel}
        </Link>
      ),
    },
    { id: 'body', header: 'Promemoria' },
    { id: 'userName', header: 'Assegnato a', hiddenByDefault: true },
    {
      id: 'createdAt',
      header: 'Creato',
      sortKey: 'createdAt',
      width: '10rem',
      hiddenByDefault: true,
      accessor: (row) => new Date(row.createdAt),
    },
  ],
  filters: [
    {
      id: 'dueState',
      label: 'Stato',
      type: 'enum',
      options: enumOptions(REMINDER_DUE_STATES, REMINDER_DUE_STATE_LABELS),
    },
    {
      id: 'subjectType',
      label: 'Tipo soggetto',
      type: 'enum',
      options: enumOptions(ACTIVITY_SUBJECTS, ACTIVITY_SUBJECT_LABELS),
    },
    {
      id: 'userId',
      label: 'Assegnato a',
      type: 'relation',
      search: searchUsers,
      resolve: resolveUser,
    },
    { id: 'scheduled', label: 'Scadenza', type: 'dateRange' },
    {
      id: 'completed',
      label: 'Chiusi',
      type: 'boolean',
      trueLabel: 'Solo chiusi',
      falseLabel: 'Solo aperti',
    },
  ],
  rowActions: [
    {
      id: 'dismiss',
      label: 'Fatto',
      icon: <Check />,
      // Dismissing is the only thing that closes a reminder; the sweep only
      // ever marks one as surfaced (docs/domain/08-activities.md).
      visible: (row) => row.completedAt === null,
      onSelect: async (row) => {
        await completeActivity(row.id);
      },
    },
  ],
  empty: {
    title: 'Nessun promemoria',
    description: 'Niente in sospeso con questi filtri.',
  },
});
